import { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInitiateWebCollectionMutation, usePremiumPaymentMutation } from '../../../data/premiumsandpayments';
import { setPaymentResponse, clearPaymentResponse, clearInstalmentToPay } from '../../../store/slice/paymentSlice';
import { configureHttpClient } from '../../../utils/payment/httpClientConfig';
import {
  initInstalments,
  initWebCollectionErrorMessage,
  hasOneFailedInit,
  totalAmountHandler,
  updateUpmInstalmentRecord
} from '../../../helpers/paymentHelper';

import { BrandContext } from '../../../contextProviders/brandContext';
import { SUMMARY, PAYMENT_ERROR } from '../../../constants/constants';
import { SLICES } from '../../../constants/slices';

export const useEnterCreditCardDetails = (instalmentToPay) => {
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const [initWebCollection] = useInitiateWebCollectionMutation();
  const [updateInstalmentInUpm] = usePremiumPaymentMutation();
  const [initWebColError, setInitWebColError] = useState();
  const [takePaymentError, setTakePaymentError] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const {
    activeBrandContext: {
      brand: { partner, broker }
    }
  } = useContext(BrandContext);

  const dispatch = useDispatch();

  const paymentDetails = {
    amount: totalAmountHandler(instalmentToPay?.PremiumInstallment),
    customerRef: instalmentToPay.PolicyNumber,
    registerBanking: true,
    usePetSure: instalmentToPay.IsPetSureUnderwriter
  };

  useEffect(() => {
    configureHttpClient(authDetails);
  }, [authDetails]);

  useEffect(() => {
    // return to step SELECT_PAYMENT when user navigates away while in ENTER_CREDIT_CARD step
    dispatch(clearInstalmentToPay());
  }, [dispatch]);

  const handleBeforeSubmit = async () => {
    setIsLoading(true);

    try {
      const initRequestIds = await initInstalments(instalmentToPay?.PremiumInstallment, initWebCollection);
      const hasZeroRequestId = initRequestIds.find((item) => item.requestId === 0);
      // Revert intiatilzed instalments if one or more instalment in selection has failed to init
      if (hasZeroRequestId) {
        const errorMessage = initWebCollectionErrorMessage(hasZeroRequestId.message);
        setInitWebColError(errorMessage);

        hasOneFailedInit(initRequestIds, updateInstalmentInUpm);
        setIsLoading(false);
        return { success: false };
      }
      return { success: true, initRequestIds };
    } catch (err) {
      setIsLoading(false);
      setTakePaymentError(PAYMENT_ERROR);
      return { success: false };
    }
  };

  const handleSubmitSuccess = async (payment, requestIds) => {
    try {
      // Handle api error if isSuccessResponse is false
      if (!payment?.isSuccessResponse && payment?.responseObject) {
        setIsLoading(false);
        setTakePaymentError(payment.responseObject.CustomerMessage);
        return;
      }
      // Update Upm record based on the payment response
      if (payment?.responseObject && requestIds) {
        await updateUpmInstalmentRecord(requestIds, updateInstalmentInUpm, payment.responseObject);

        const totalAmountPaid = paymentDetails.amount;
        const activeStep = SUMMARY;
        dispatch(clearPaymentResponse());
        dispatch(setPaymentResponse({ totalAmountPaid, activeStep, payment }));

        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      setTakePaymentError(PAYMENT_ERROR);
    }
  };

  const handleSubmitFail = () => {
    setTakePaymentError(PAYMENT_ERROR);
    setIsLoading(false);
  };

  return {
    initWebColError,
    takePaymentError,
    isLoading,
    paymentDetails,
    handleBeforeSubmit,
    handleSubmitSuccess,
    handleSubmitFail,
    partner,
    broker
  };
};
